/**
 * Roaster profiles for the Our Roasters section.
 *
 * TO UPDATE WHAT'S ON BAR: edit `currentlyFeaturing` on the relevant roaster.
 * That string drives the callout on the profile page — no component changes
 * needed.
 *
 * TO ADD A ROASTER: append an object below. The hub cards and the
 * /roasters/:slug template both read from this array, so a new entry appears
 * in both places automatically. Remember to add the new URL to
 * public/sitemap.xml.
 *
 * TO ADD PHOTOGRAPHY: set `image` to a URL. Until then each page renders a
 * styled placeholder in the same slot.
 */

export type Roaster = {
  slug: string
  /** Roaster name, e.g. 'Proyecto Diaz'. */
  name: string
  /** City and state, shown as the location tag. */
  location: string
  /** Roaster's own website, used in the Brand schema. */
  brandUrl: string
  /** One-line summary for the hub card. */
  cardSummary: string
  /** What we pour from them, shown on the hub card. */
  cardRole: string
  /** <title> for the profile page. */
  metaTitle: string
  /** meta[name=description] for the profile page. */
  metaDescription: string
  /** H1 on the profile page. */
  heading: string
  /** Main story copy. */
  story: string
  /** 'Why we feature them' copy. */
  whyWeFeatureThem: string
  /** The easy-to-update 'Currently featuring' callout. */
  currentlyFeaturing: string
  /** Optional photo URL. Leave undefined to render the placeholder. */
  image?: string
  /** Alt text used when `image` is set. */
  imageAlt?: string
}

export const roasters: Roaster[] = [
  {
    slug: 'proyecto-diaz',
    name: 'Proyecto Diaz',
    location: 'Oakland, CA',
    brandUrl: 'https://www.proyectodiazcoffee.com',
    cardSummary: "A Oaxacan family's farm-to-cup legacy.",
    cardRole: 'Our drip & decaf espresso, plus a wide retail selection.',
    metaTitle:
      'Proyecto Diaz Coffee in Livermore - Our Drip & Decaf | Grounded',
    metaDescription:
      "Grounded pours Proyecto Diaz on drip (El Carmen) and decaf espresso (Ebb and Flow), with a wide retail selection. Meet the Oakland roaster carrying a Oaxacan family's legacy.",
    heading: 'Proyecto Diaz - A Family Legacy, Farm to Cup',
    story:
      "Some coffee has a family behind it. Proyecto Diaz is the family. Founded in Oakland in 2014 by Fernando and Hannah-Love Diaz - with Fernando's father, Genaro, as head roaster - Proyecto Diaz carries a coffee legacy that stretches back to the early 1900s, when the family began farming El Carmen in the Sierra Sur mountains of Oaxaca, Mexico. Watching small producers struggle under commodity prices, the family built Proyecto Diaz to do it differently: direct trade, prices well above commodity, and a portion of profits reinvested straight back into El Carmen and partner farms. Their motto says it plainly - caffeinate with purpose.",
    whyWeFeatureThem:
      "It's everything we love in a roaster - family-owned, deeply traceable, and mission-driven - and the cup is bright, balanced, and genuinely joyful to drink all day.",
    currentlyFeaturing:
      'El Carmen on our drip (a bright, balanced medium-light roast) and Ebb and Flow on decaf espresso - plus a wide selection of Proyecto Diaz bags on our retail shelf.',
    image:
      'https://cdn.magicpatterns.com/uploads/jEE6c5SyCpn1tiJyopUuuR/proyecto-diaz.jpg',
    imageAlt: 'Proyecto Diaz Coffee logo',
  },
  {
    slug: 'temple-coffee',
    name: 'Temple Coffee',
    location: 'Sacramento, CA',
    brandUrl: 'https://templecoffee.com',
    cardSummary: 'Farm-to-cup pioneers.',
    cardRole: 'Our house espresso, plus a wide retail selection.',
    metaTitle: 'Temple Coffee in Livermore - Our House Espresso | Grounded',
    metaDescription:
      "Grounded pulls its house espresso with Temple's Dharma roast, and carries a wide selection of Temple retail bags. Meet Sacramento's farm-to-cup pioneers, in Livermore, CA.",
    heading: "Temple Coffee - Sacramento's Farm-to-Cup Pioneers",
    story:
      "Pull a shot at Grounded and you're tasting Temple. Founded in Sacramento in 2005 by Sean Kohmescher after travels through Indonesia, Temple was built on the idea that a coffee shop could be a kind of temple - a sacred, everyday gathering place. Two decades and nine Sacramento-area cafes later, they're known for a rigorous Farm to Cup model built on three pillars of sustainability: social, economic, and environmental. They pay well above Fair Trade, favor farms that protect water and forest, and bag their beans in plant-based, compostable packaging. Publications like Gear Patrol have named them among the best roasters in America.",
    whyWeFeatureThem:
      'Their Dharma espresso is our house shot for a reason - dried cherry, baking spice, and cocoa that carry beautifully through a latte or stand tall on their own. Responsibly sourced, expertly roasted, endlessly drinkable.',
    currentlyFeaturing:
      'Dharma as our house espresso, plus a wide selection of Temple bags on our retail shelf.',
    image:
      'https://cdn.magicpatterns.com/uploads/dsjvWsNmrBoT6UFKYs37AU/temple.jpg',
    imageAlt: 'Temple Coffee logo',
  },
  {
    slug: 'chromatic-coffee',
    name: 'Chromatic Coffee',
    location: 'San Jose, CA',
    brandUrl: 'https://www.chromaticcoffee.com',
    cardSummary:
      "Precise, small-batch craft from the Valley of Heart's Delight.",
    cardRole: 'Our cold brew, plus select retail.',
    metaTitle: 'Chromatic Coffee in Livermore - Our Cold Brew | Grounded',
    metaDescription:
      "Grounded's cold brew is Chromatic's Opus, and we carry select Chromatic retail. Meet the San Jose roaster from the Valley of Heart's Delight, in Livermore, CA.",
    heading: "Chromatic Coffee - Precision from the Valley of Heart's Delight",
    story:
      "That smooth, chocolatey cold brew you keep coming back for? That's Chromatic. Chromatic Coffee roasts in San Jose, in the heart of what was once called the Valley of Heart's Delight - a nod to the region's orchard past that shows up in their branding and their beans. Founded in 2012, they've built a reputation for small-batch precision, direct farmer relationships, and a hands-on, DIY craft culture, down to hand-stamped bags and a cafe that doubles as an arts space. Locals have repeatedly voted them among Silicon Valley's best roasters.",
    whyWeFeatureThem:
      "Their Opus makes a cold brew that's exactly what we want - slow-steeped, smooth, and rich with chocolate - the kind of glass that disappears faster than you meant it to on a warm Livermore afternoon.",
    currentlyFeaturing:
      'Opus on our cold brew, plus select Chromatic bags on our retail shelf.',
    image:
      'https://cdn.magicpatterns.com/uploads/mBh7nrEZZBQ96QZjY9NTsk/chromatic.jpg',
    imageAlt: 'Chromatic Coffee Co. logo',
  },
  {
    slug: 'cat-and-cloud',
    name: 'Cat & Cloud',
    location: 'Santa Cruz, CA',
    brandUrl: 'https://catandcloud.com',
    cardSummary: 'Hospitality-obsessed, community-built.',
    cardRole: 'Our pour-over rotation, plus select retail.',
    metaTitle:
      'Cat & Cloud Coffee in Livermore - Pour-Over & Retail | Grounded',
    metaDescription:
      'Grounded features Cat & Cloud on our pour-over rotation and select retail - including the Brazil Cavaquinho pulp-natural and the Best of Friends Blend. Meet the Santa Cruz roaster built on hospitality.',
    heading: 'Cat & Cloud - Coffee Built on Hospitality',
    story:
      'Some roasters are about the bean. Cat & Cloud is about the experience. Started in the Santa Cruz area around 2015 by three friends - including two who came up through Verve and now host the widely followed Cat & Cloud Podcast - the company was founded on a simple belief: that experiences and connection shape our lives, and coffee is a great way to create them. Their whole culture is built on hospitality and leaving people in a better place than we found them - a philosophy that feels a lot like our own.',
    whyWeFeatureThem:
      "Kindred spirits - their whole approach is built on hospitality. Their coffees are a joy to showcase on our pour-over bar, where a single, carefully brewed cup can turn a quick stop into the best part of someone's morning.",
    currentlyFeaturing:
      'Cat & Cloud on our pour-over rotation - featuring beans like their Brazil Cavaquinho (a juicy pulp-natural) and the Best of Friends Blend - plus a select retail selection.',
    image:
      'https://cdn.magicpatterns.com/uploads/a7ZVCgc2oFebc5uf6awLmR/cat-and-cloud.jpg',
    imageAlt: 'Cat & Cloud Coffee logo',
  },
  {
    slug: 'daybreak',
    name: 'Daybreak',
    location: 'San Mateo, CA',
    brandUrl: 'https://www.enjoyadaybreak.com',
    cardSummary: 'Authentic butter-roasted Vietnamese coffee.',
    cardRole: 'Our Vietnamese coffee (instant coming to retail).',
    metaTitle: 'Daybreak Vietnamese Coffee in Livermore | Grounded',
    metaDescription:
      'Grounded\u2019s Vietnamese coffee is roasted by Daybreak - authentic, butter-roasted Dak Lak robusta. Real Vietnamese coffee in Livermore, CA, with Daybreak instant coming to retail.',
    heading: 'Daybreak - Authentic Vietnamese Coffee',
    story:
      "That rich, bold Vietnamese cold brew you love? That's Daybreak. Daybreak (Daybreak Goods, out of San Mateo) was founded in 2022 by Michelle Tu and Stanislav Kroll to bring authentic Vietnamese coffee to a wider audience - born from family trips to Vietnam and the difficulty of finding the real thing stateside. They source 100% robusta from a third-generation family farm in Dak Lak, Vietnam, and roast it in butter the traditional way, for a cup that's strong, bold, and unmistakably Vietnamese.",
    whyWeFeatureThem:
      "Vietnamese coffee is its own beautiful tradition, and we wanted to serve the real thing - not an imitation. Daybreak's butter-roasted robusta, poured over sweetened condensed milk, is exactly that.",
    currentlyFeaturing:
      'Daybreak in our Vietnamese Cold Brew - robusta over sweetened condensed milk, strong and creamy. Coming soon: Daybreak instant coffee on our retail shelf.',
    image:
      'https://cdn.magicpatterns.com/uploads/oJ6NNrXYdWPrXdPxP1586o/daybreak.png',
    imageAlt: 'Daybreak Vietnamese Coffee logo',
  },
]

export function getRoasterBySlug(slug: string): Roaster | undefined {
  return roasters.find((roaster) => roaster.slug === slug)
}
